export enum ProductPackage {
  Full = 'FULL',
  FullWithoutErrorCodes = 'FULL_WITHOUT_ERROR_CODES',
  Basic = 'BASIC',
  BasicLegacy = 'BASIC_LEGACY',
}

export type ProductPackages = Record<string, ProductPackage | null>;

/** Narrows a raw ui-config value to a known package, or `null` when it is missing or unrecognised. */
export const parseProductPackage = (value: string | null | undefined): ProductPackage | null =>
  Object.values(ProductPackage).find((productPackage) => productPackage === value) ?? null;

/** Narrows the raw ui-config `productPackages` map, keeping systems whose package is unrecognised as `null`. */
export const parseProductPackages = (packages: Record<string, string> | null | undefined): ProductPackages =>
  Object.fromEntries(
    Object.entries(packages ?? {}).map(([systemBk, productPackage]) => [systemBk, parseProductPackage(productPackage)])
  );

export class UiConfig {
  customerId: string;
  featureFlags: string[] | FeatureFlagsEnum[];
  customerType: CustomerType;
  solutions: Solution[];
  customerBk: string;
  productPackages: ProductPackages;
}

export class LoginConfig {
  awsRegion: string;
  userPoolId: string;
  appClientId: string;
  ssoConfig?: {
    identityProvider: string;
    domain: string;
    allowedHost: string;
    redirectUrl: string;
  };
}

export enum CustomerType {
  ENERGY = 'ENERGY',
  MOBILITY = 'MOBILITY',
  MODELS = 'MODELS',
  // Only used as a fallback if config is missing or invalid
  UNKNOWN = 'UNKNOWN',
}

export enum Solution {
  ENERGY = 'ENERGY',
  MOBILITY = 'MOBILITY',
  MODEL_LIBRARY = 'MODEL_LIBRARY',
}

export enum FeatureFlagsEnum {
  ENERGY_PERFORMANCE_MANAGER_SHOW_BALANCING_SELECTOR = 'ENERGY_PERFORMANCE_MANAGER_SHOW_BALANCING_SELECTOR',
  ENERGY_PERFORMANCE_MANAGER_DISABLE_FINANCIAL_IMPACT = 'ENERGY_PERFORMANCE_MANAGER_DISABLE_FINANCIAL_IMPACT',
  FLEET_ANALYTICS_HIDE_WARRANTY = 'FLEET_ANALYTICS_HIDE_WARRANTY',
  FLEET_ANALYTICS_ENABLE_SAFETY = 'FLEET_ANALYTICS_ENABLE_SAFETY',
  MODO_ENERGY_INTEGRATION = 'MODO_ENERGY_INTEGRATION',
  SYSTEM_LEVEL_RTE = 'SYSTEM_LEVEL_RTE',
  RULE_CONFIGURATION = 'RULE_CONFIGURATION',
  OVERVIEW_MAP = 'OVERVIEW_MAP',
  DE_CLONE_VIEWS = 'DE_CLONE_VIEWS',
  HIDE_PENALTY_RISK = 'HIDE_PENALTY_RISK',
  DE_SHOW_ALERTS = 'DE_SHOW_ALERTS',
  ENABLE_OTHER_COMPONENT_IN_SMART_ALERTS = 'ENABLE_OTHER_COMPONENT_IN_SMART_ALERTS',
  DIGITAL_ONBOARDING_SUPPORT_MODE = 'DIGITAL_ONBOARDING_SUPPORT_MODE',
  ADVANCED_SMART_ALERTS = 'ADVANCED_SMART_ALERTS',
  ENABLE_INSIGHTS_AND_ACTIONS = 'ENABLE_INSIGHTS_AND_ACTIONS',
}

export interface ConfigsInterface {
  featureFlags: string[] | FeatureFlagsEnum[];
  solutions: Solution[];
}

export interface ComponentNameMapping {
  [key: string]: string;
}

export interface LevelNameInfo {
  name: string;
  namePlural: string;
  defaultName: string;
  defaultNamePlural: string;
}

export enum StorageLevel {
  Portfolio = 'portfolio',
  System = 'system',
  Cluster = 'cluster',
  Inverter = 'inverter',
  String = 'string',
  Module = 'module',
  Cell = 'cell',
  InverterModule = 'inverter_module',
}

export enum StorageLevelV2 {
  Cell = 'cell',
  StringModule = 'string_module',
  StringModulePCS = 'string_module_pcs',
  String = 'string',
  StringPCS = 'string_pcs',
  StringGroup = 'string_group',
  DCCombiner = 'dc_combiner',
  PCSModule = 'pcs_module',
  PCS = 'pcs',
  ACCombiner = 'ac_combiner',
  Enclosure = 'enclosure',
  Transformer = 'transformer',
  System = 'system',
  Site = 'site',
}

export type LevelNameMapping = {
  [key in StorageLevel | StorageLevelV2]: LevelNameInfo;
};

export interface StorageNameMapping {
  levelNameMapping: LevelNameMapping;
  componentNameMapping: ComponentNameMapping;
}
